import { Card, CardContent, Stack, Typography } from '@mui/material';
import { t } from 'i18next';
import { ReactNode } from 'react';
import { TagCell } from '../../common/components/Cells/TagCell';
import { UserLevel, getUserLevelLabel } from '../../organization/users/enums/UserLevel';
import { UserRole, getUserRoleLabel } from '../../organization/users/enums/UserRole';
import { Project } from '../models/Project';
import { Report } from '../models/Report';
import { Slot } from '../models/Slot';
import { CopyPassword } from './CopyPassword';

type FormSummaryProps = {
  project?: Project | null;
  slot?: Slot | null;
  report?: Report | null;
  user?: { name: string; preferredName: string | null; level: UserLevel | null; roles: UserRole[] | null; password: string };
};

const SummarySection = (props: { label: string; value: ReactNode }) => {
  return (
    <Stack direction="row" alignItems="center" sx={{ width: 1 }}>
      <Typography component="span" fontWeight="bold">
        {props.label}:
      </Typography>
      <Typography component={typeof props.value === 'string' ? 'span' : 'div'} sx={{ pl: 1, flexGrow: 1 }}>
        {props.value}
      </Typography>
    </Stack>
  );
};

export const FormSummary = ({ project, slot, report, user }: FormSummaryProps) => {
  return (
    <Card className="form-summary" sx={{ bgcolor: 'grey.100' }}>
      <CardContent>
        <Stack spacing={1.5}>
          {project && <SummarySection label={t(`creationFormSummary.project`)} value={project.getName()} />}
          {project?.getDescription() && <SummarySection label={t(`creationFormSummary.projectDescription`)} value={project.getDescription()!} />}
          {slot && <SummarySection label={t(`creationFormSummary.slot.slot`)} value={slot.getName()} />}
          {report && (
            <>
              <SummarySection label={t(`creationFormSummary.slot.report`)} value={report.getName()} />
              <SummarySection label={t(`creationFormSummary.trace`)} value={report.getTrace()?.getName() ?? ''} />
            </>
          )}
          {user && (
            <>
              <SummarySection label={t(`username`)} value={user.name} />
              {user.preferredName && <SummarySection label={t(`preferredName`)} value={user.preferredName} />}
              {user.roles && <SummarySection label={t(`userRoles.description`)} value={<TagCell tags={user.roles.map(getUserRoleLabel)} />} />}
              {user.level && <SummarySection label={t(`userLevels.description`)} value={getUserLevelLabel(user.level)} />}
              <SummarySection label={t('password')} value={<CopyPassword password={user.password} hidden size="small" />} />
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};
